// --- React & Redux
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
// --- Actions & Hooks
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
import { useProductsActions } from 'datastage/store/products/useProducts';
// --- External UI Libraries
import DataGrid from '@dtci/spellbook/DataGrid';
// --- Components
import { ProductDetailsDrawer } from '../details-drawer';
// --- Utilities
import { useProductsGridUtils } from './utils/gridUtils';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';

/**
 * Products Grid
 *
 * Features:
 * - Displays a data grid of products using AG Grid
 * - Integrates with Redux for product state management
 * - Opens a details drawer for individual product details
 *
 * @returns {JSX.Element}
 */
export const ProductsGrid = () => {
    const agGridApi = useAgGridApi();
    const { updateProducts } = useProductsActions();
    const { products } = useSelector((s) => s.products);
    const showSpinner = useSelector((s) => s.spinner.showSpinner);

    // Handle opening the details drawer with specific product details
    const onOpenDetailsDrawer = useCallback(
        (data) => {
            updateProducts({ isDetailsOpen: true, details: data });
        },
        [updateProducts],
    );

    // Utilize custom hook for grid utilities
    const { columns } = useProductsGridUtils({
        columnHandlers: [onOpenDetailsDrawer],
    });

    return (
        <>
            <DataGrid
                className={styles.dataGrid}
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: products,
                    headerHeight: 45,
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: { 
                            paddingTop: '10px', 
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={showSpinner ? 'Loading...' : 'No Products Found'}
            />
            <ProductDetailsDrawer />
        </>
    );
};
